import { getAuth } from 'firebase/auth';
import { devSimulatedHeaders } from '../../../dev/devSimulated';
import { reportIfLegalRequired } from '../../../legal/legalRequired';

const INSIGHTS_API_URL =
    (import.meta.env.VITE_INSIGHTS_API_URL as string | undefined) ?? '';

const BASE = import.meta.env.DEV ? '' : INSIGHTS_API_URL;

if (import.meta.env.DEV && !INSIGHTS_API_URL) {
    console.warn(
        'VITE_INSIGHTS_API_URL is not set. The dev /api proxy has no target, ' +
        'so Insights API calls will fail. Add it to your .env (e.g. ' +
        'VITE_INSIGHTS_API_URL=http://localhost:8000).'
    );
}

export class ApiError extends Error {
    constructor(
        public status: number,
        message: string
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

async function assertOk(res: Response): Promise<Response> {
    if (!res.ok) {
        const text = await res.text().catch(() => 'Unknown error');
        const error = new ApiError(res.status, `API ${res.status}: ${text}`);
        // Every proxied request passes here, so this is where the app learns
        // mid-session that the user's legal acceptance has run out.
        reportIfLegalRequired(error);
        throw error;
    }
    return res;
}

async function handleResponse<T>(res: Response): Promise<T> {
    return (await assertOk(res)).json() as Promise<T>;
}

async function authHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...devSimulatedHeaders(),
    };
    const token = await getAuth().currentUser?.getIdToken();
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
    const res = await fetch(`${BASE}${path}`, {
        signal,
        headers: await authHeaders(),
    });
    return handleResponse<T>(res);
}

// Binary counterpart to apiGet, for bytes the browser would otherwise fetch
// by itself. An <img> pointed straight at a proxied path carries no
// Authorization header, and the proxy authenticates on that header only, so
// it answers 401 instead. Going through fetch() attaches the token; the
// response still lands in the HTTP cache, so the endpoint's long immutable
// max-age keeps working across mounts.
export async function apiGetBlob(
    path: string,
    signal?: AbortSignal
): Promise<Blob> {
    // authHeaders() declares a JSON body for the POST case; this request has
    // no body at all, and the proxy forwards these headers to the indexer
    // verbatim. The bearer token and simulated flag still apply.
    const headers = await authHeaders();
    delete headers['Content-Type'];
    const res = await fetch(`${BASE}${path}`, { signal, headers });
    return (await assertOk(res)).blob();
}

export async function apiPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const res = await fetch(`${BASE}${path}`, {
        method: 'POST',
        signal,
        headers: await authHeaders(),
        body: JSON.stringify(body),
    });
    return handleResponse<T>(res);
}

// Text counterpart to apiPost, for answers that are not JSON (the trader
// download pages are CSV or JSON lines). The content type comes back with the
// text so that the caller can tell an old indexer's JSON answer from a page.
export async function apiPostText(
    path: string,
    body: unknown,
    signal?: AbortSignal
): Promise<{ text: string; contentType: string }> {
    const res = await fetch(`${BASE}${path}`, {
        method: 'POST',
        signal,
        headers: await authHeaders(),
        body: JSON.stringify(body),
    });
    await assertOk(res);
    return {
        text: await res.text(),
        contentType: res.headers.get('content-type') ?? '',
    };
}
